import { useLazyQuery, useMutation } from '@apollo/client';
import { Box, Collapse, Fade, Flex, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { useSilenceAEP } from '@components/crm/shared/queries';
import Spinner from '@components/icons/Spinner';
import Button, { ButtonVariants } from '@components/interactive/Button';
import WizardLayout from '@components/layout/layout/WizardLayout';
import Body1 from '@components/typography/Body1';
import Header from '@components/typography/Header';
import { Routes } from '@constants/routes';
import { useAuthenticatedContextProvider } from '@contexts/AuthenticatedContextProvider';
import { CallUsReason } from '@enums/CallUsReason';
import { CurrentPlanRanking } from '@enums/CurrentPlanRanking';
import { PlanRecommendationContext } from '@enums/PlanRecommendationContext';
import { PlanType } from '@enums/PlanType';
import GET_PLAN_RECOMMENDATIONS from '@gql/GetPlanRecommendations';
import GET_SAML_RESPONSE_BODY from '@gql/SAMLResponseBody';
import TOGGLE_USE_IN_RECOMMENDATIONS from '@gql/ToggleUseInRecommendations';
import { debounce } from '@utils/debounce';
import getPrimaryPlan from '@utils/getPrimaryPlan';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import Confetti from 'react-confetti';
import { Pharmacy as PharmacyType } from 'types/Pharmacy';
import { useAepWizard } from '@contexts/AepWizardContext';
import AepBreadCrumbs from '../AepBreadCrumbs';
import DrxGuide from './DrxGuide';
import Filters from './Filters';
import DrugNotCoveredModal from './modals/DrugNotCoveredModal';
import {
  ContentState,
  Filter,
  FilterType,
  SAVINGS_THRESHOLD,
  createFilterPersistenceData,
  createFilters,
  formatNumber,
  getContentState,
  getPlanTypeCopy,
  getRelayState,
  restoreFilterData,
} from './utils';
import { extractCmsId, formatCMSId } from '@components/crm/utils';

const RecommendationReview = ({ bypassBreadcrumb = false }) => {
  // Resources
  const { authenticatedUser: user } = useAuthenticatedContextProvider();
  const { notCorrectPlan } = useAepWizard();
  const plan = getPrimaryPlan(user);
  const planType = plan?.plan_type;
  const planSubType = plan?.plan_sub_type;
  const planName = plan?.name;
  const isMapd = planType === PlanType.MAPD;

  const router = useRouter();
  const { pathname, query } = router;
  const { isOpen: isNotCoveredOpen, onOpen: onNotCoveredOpen, onClose: onNotCoveredClose } = useDisclosure();
  // State
  const [recommendations, setRecommendations] = useState([]);
  const [loadedPlans, setLoadedPlans] = useState(false);
  const [, setNotCoveredDismissed] = useState(false);
  const [showDrxGuide, setShowDrxGuide] = useState(false);
  const [pharmacy, setPharmacy] = useState<PharmacyType | null>(null);
  const [primaryProviders, setPrimaryProviders] = useState<Filter[]>([]);
  const [specialistProviders, setSpecialistProviders] = useState<Filter[]>([]);
  const [prescriptions, setPrescriptions] = useState<Filter[]>([]);
  const [currentPlanRanking, setCurrentPlanRanking] = useState<CurrentPlanRanking | null>(null);
  const [currentPlanDetails, setCurrentPlanDetails] = useState<any | null>(null);
  const [forceFilters, setForceFilters] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [error, setError] = useState(false);

  let recYear;
  if (plan || !user) {
    recYear = new Date().getFullYear() + 1;
  } else if (user.effectiveDate) {
    recYear = new Date(user.effectiveDate).getFullYear();
  } else {
    recYear = new Date().getFullYear();
  }

  const wizardType: PlanRecommendationContext =
    router.pathname === '/account/aep/recommendations'
      ? PlanRecommendationContext.AEP_WIZARD
      : PlanRecommendationContext.WIZARD;

  const includeHMOInSearch = isMapd && !planSubType.includes('PPO');

  const handleNotCoveredClose = () => {
    const toUncheck = prescriptions.filter(
      (prescription) => prescription.inNetworkPercentage === 0 && prescription.checked
    );

    toUncheck.forEach((prescription) => onFilterChange(FilterType.prescriptions, prescription.value, false));

    setNotCoveredDismissed(true);
    onNotCoveredClose();
  };

  // Handle onCompleted for GET_PLAN_RECOMMENDATIONS request.
  const dataLoaded = (data: any, initialLoad: boolean) => {
    const {
      user: userFromRec,
      inNetworkCoverage,
      currentPlanRanking,
      recommendations,
      currentPlanDetails,
    } = data.getPlanRecommendations;

    const currentPlanId = currentPlanDetails?.cmsId;

    setCurrentPlanDetails(currentPlanDetails);
    setPharmacy(userFromRec?.pharmacies[0]);
    setRecommendations(recommendations);
    setCurrentPlanRanking(currentPlanRanking);

    const newFilters = createFilters(userFromRec.prescriptions, userFromRec.providers, inNetworkCoverage);

    const filtersInMem = JSON.parse(window.localStorage.getItem('pscripFilters'));

    // Restore filter data from localStorage persistence
    const restoredFilters = restoreFilterData(newFilters, filtersInMem);

    setPrescriptions(restoredFilters.prescriptions);
    setPrimaryProviders(restoredFilters.primaries);
    setSpecialistProviders(restoredFilters.specialists);

    // Fetch SAML data now so it's ready for when the user clicks the DRX button.
    if (recommendations.length) {
      loadSAML({
        variables: {
          userId: user.id,
          year: recYear,
        },
      });
    }

    // If initial load, create stored inNetworkPercentage map & display modal if
    // any equal 0%. Also display, Top Rec modal if appropriate
    if (initialLoad) {
      const persistenceData = createFilterPersistenceData(restoredFilters);
      window.localStorage.setItem('pscripFilters', JSON.stringify(persistenceData));

      const toUncheck = restoredFilters.prescriptions.filter(
        (pscrip) => pscrip.inNetworkPercentage === 0 && pscrip.checked
      );

      if (toUncheck.length) {
        onNotCoveredOpen();
      }

      setLoadedPlans(true);
      setForceFilters(false);
    }
    setError(false);
  };

  // GET_SAML_RESPONSE_BODY request
  const [loadSAML, { loading: samlLoading, data: samlData }] = useLazyQuery(GET_SAML_RESPONSE_BODY, {
    fetchPolicy: 'no-cache',
  });

  const [getPlans, { loading: loadingPlans, data: plansData }] = useLazyQuery(GET_PLAN_RECOMMENDATIONS, {
    fetchPolicy: 'no-cache',
    variables: {
      wizardType,
      effectiveDate: user?.effectiveDate,
      ignoreNetworkCoverage: false,
      includeHMOInSearch,
      planType,
      userId: user?.id,
      zipCode: user?.zip_code,
      bypassCurrentPlan: notCorrectPlan,
    },
    onCompleted: (data) => dataLoaded(data, true),
    onError: () => {
      setError(true);
    },
  });

  // Create a debounced version of the plan search that persists across renders
  const debouncedGetPlansRef = useRef<(() => void) | null>(null);

  if (!debouncedGetPlansRef.current) {
    debouncedGetPlansRef.current = debounce(getPlans, 700);
  }

  // TOGGLE_USE_IN_RECOMMENDATIONS request for filter toggle.
  const [toggleUseInRecommendations] = useMutation(TOGGLE_USE_IN_RECOMMENDATIONS, {
    onCompleted: () => debouncedGetPlansRef.current?.(),
  });

  useEffect(() => {
    if (user) {
      getPlans();
    }
  }, [user, getPlans]);

  const onFilterChange = (type: FilterType, id: string, checked: boolean) => {
    const filter: Filter = { type, value: id, checked };

    updateFilter(type, filter);

    toggleUseInRecommendations({
      variables: {
        id,
        type,
        includeInRecommendations: checked,
      },
    });
  };

  // Call update routine w respective state function
  const updateFilter = (type: FilterType, newFilter: Filter) => {
    switch (type) {
      case FilterType.prescriptions:
        setPrescriptions((prev) => updateRoutine(prev, newFilter));
        break;
      case FilterType.primaryProviders:
        setPrimaryProviders((prev) => updateRoutine(prev, newFilter));
        break;
      case FilterType.specialistProviders:
        setSpecialistProviders((prev) => updateRoutine(prev, newFilter));
        break;
      default:
        break;
    }
  };

  // Routine for setting old filter properties to new filter values
  const updateRoutine = (prevState: Filter[], newFilter: Filter) =>
    prevState.map((filter) => {
      if (filter.value === newFilter.value) {
        return {
          ...filter,
          ...newFilter,
        };
      }
      return {
        ...filter,
      };
    });

  // Variables
  // Find the recommendation with the highest savings value.
  const topRecommendation = recommendations.reduce((max, recommendation) => {
    if (recommendation.__typename === 'Recommendation' && (!max || recommendation.savings > max.savings)) {
      return recommendation;
    }
    return max;
  }, null);

  // Get the recommendations with savings relative to the switch case.
  // Optimal: we want all recommendations, even those with negative savings.
  // Other cases: we only want recommendations with savings >= 0
  const recommendationsWithSavings =
    currentPlanRanking === CurrentPlanRanking.TOP_PLAN_IN_REC || recommendations.every((rec) => rec.savings <= 0)
      ? recommendations
      : recommendations.filter((recommendation) => {
          return recommendation.savings >= 0;
        });

  const currentPlanCmsId = useMemo(() => {
    const rawCmsId = plan?.cms_id;
    if (!rawCmsId) return null;
    const parts = rawCmsId.split('_');
    if (parts.length < 3) return null;
    const [hNumber, planId, segMentId] = parts;
    return formatCMSId(hNumber, planId, segMentId, '-');
  }, [plan?.cms_id]);

  const recommendationsWithoutCurrentPlan = recommendationsWithSavings.filter((r) => {
    if (!currentPlanCmsId) return true;
    return r.id !== currentPlanCmsId;
  });

  const currentPlanDrugsCovered = currentPlanDetails?.drugsNotCovered.length === 0;
  const drugsNotCovered = currentPlanDetails?.drugsNotCovered;
  const allProvidersInNetwork = currentPlanDetails?.allProvidersInNetwork;
  const currentPlanNotFound = !currentPlanDetails;

  const loadingRedirect = samlLoading || loadingPlans;
  const relayState = useMemo(
    () =>
      getRelayState(
        recYear,
        recommendationsWithSavings,
        user?.zip_code,
        plansData?.getPlanRecommendations?.countyFIPS,
        currentPlanDetails?.cmsId
      ),
    [recommendations, user, plansData]
  );

  // Determine Content State
  const contentState = useMemo(() => {
    return getContentState({
      currentPlanNotFound,
      allProvidersInNetwork,
      currentPlanDrugsCovered,
      currentPlanRanking,
      loadedPlans,
      loadingPlans,
      recommendations,
      drugsNotCovered,
      topSavings: topRecommendation?.savings,
      planType,
    });
  }, [recommendations, currentPlanRanking, loadedPlans, loadingPlans]);

  const ParetoSubheader = () => {
    const savingsString = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
      topRecommendation.savings
    );

    return (
      <Stack>
        <Body1>
          Your current plan is {planName} and its {recYear} premium and drug cost is not the lowest of all plans that
          cover your {getPlanTypeCopy(planType)}, but it's close. We estimate your plan will cost{' '}
          <Box as="span" fontWeight="bold">
            {savingsString}
          </Box>{' '}
          more per year than the lowest cost plan.
        </Body1>
      </Stack>
    );
  };

  const OptimalSubheader = () => (
    <Stack>
      <Body1>
        <>
          <Text as="span">Your current plan - </Text>
          <Text as="span" fontWeight="bold">
            {planName}
          </Text>
          {` - has the lowest premium ${planType !== PlanType.MA ? 'and drug cost  ' : ' '}of all plans that cover your
          ${getPlanTypeCopy(planType)} in ${recYear}. Congrats! You don't need to do a thing this AEP.`}
        </>
      </Body1>
    </Stack>
  );

  const drugsNotCoveredSubheader = () => (
    <Stack>
      <Body1>
        Your current plan is {planName}, and it does not cover all the drugs we have on file. Please review your plan
        options to ensure you have the best coverage for {recYear}.
      </Body1>
    </Stack>
  );

  // Content State to Text Map
  const stateToHeaders = {
    [ContentState.Loading]: {
      header: 'Searching plans...',
      subheader: 'Looking at all plans in your zip code.',
      renderActions: () => <Button isDisabled>Loading...</Button>,
    },
    [ContentState.NoPlans]: {
      header: "Unfortunately, we couldn't find any plans that cover all of your doctors.",
      subheader: 'Please uncheck the doctors who are least important to you.',
      renderActions: () => <Button isDisabled>No Plans</Button>,
    },
    [ContentState.NoCurrentPlan]: {
      header: 'Shop Plans in Your Area',
      subheader:
        'We found the lowest cost plans in your area. Click below to see your options, and possibly switch to a lower cost plan.',
      renderActions: () => (
        <>
          <Button variant={ButtonVariants.secondary} id="aep-recs-no-current-plan" href={Routes.aepOptOut}>
            Stay On My Current Plan
          </Button>
          <Button id="aep-recs-no-current-plan" onClick={() => setShowDrxGuide(true)}>
            Show Me Plans
          </Button>
        </>
      ),
    },
    // Toggle between "Good News! We found two plans" || "Good News! We found one plan"
    [ContentState.SwitchRecommended]: {
      header:
        topRecommendation?.savings === 0
          ? `Good News! We found ${formatNumber(
              recommendationsWithoutCurrentPlan.length > 2 ? 2 : recommendationsWithoutCurrentPlan.length
            )} ${recommendationsWithoutCurrentPlan.length > 1 ? 'plans' : 'plan'} with better coverage for you.`
          : `Good News! We found ${formatNumber(
              recommendationsWithoutCurrentPlan.length > 2 ? 2 : recommendationsWithoutCurrentPlan.length
            )} ${recommendationsWithoutCurrentPlan.length > 1 ? 'lower cost plans' : 'lower cost plan'} for you.`,
      subheader:
        topRecommendation?.savings === 0
          ? `These plans offer better coverage for your ${getPlanTypeCopy(
              planType
            )} compared to your current plan, ${planName}.`
          : `These plans cover your ${getPlanTypeCopy(
              planType
            )} and could save you $${SAVINGS_THRESHOLD} or more in ${recYear} vs. your current plan, ${planName}.`,
      renderActions: () => (
        <Button id="aep-recs-see-plans" onClick={() => setShowDrxGuide(true)}>
          See Plans
        </Button>
      ),
    },
    [ContentState.Optimal]: {
      header: "Your coverage is great. Don't change a thing.",
      subheader: OptimalSubheader,
      renderActions: () => (
        <>
          <Button
            id="aep-recs-see-plans-anyway"
            variant={ButtonVariants.secondary}
            onClick={() => setShowDrxGuide(true)}
          >
            Show Me Plans Anyway
          </Button>
          <Button id="aep-recs-dashboard" href={Routes.aepOptOut}>
            I'm Happy With My Plan
          </Button>
        </>
      ),
    },
    [ContentState.Pareto]: {
      header: "You're on one of the lowest-cost plans available.",
      subheader: ParetoSubheader,
      renderActions: () => (
        <>
          <Button
            id="aep-recs-see-plan-options"
            variant={ButtonVariants.secondary}
            onClick={() => setShowDrxGuide(true)}
          >
            {topRecommendation.savings === 0 ? 'See Plan Options' : 'See Lowest-Cost Plan'}
          </Button>
          <Button id="aep-recs-stay-on-plan" href={Routes.aepOptOut}>
            Stay On My Current Plan
          </Button>
        </>
      ),
    },
    [ContentState.drugsNotCovered]: {
      header: "Your current plan doesn't cover all of your drugs.",
      subheader: drugsNotCoveredSubheader,
      renderActions: () => (
        <>
          <Button id="aep-recs-see-plans" onClick={() => setShowDrxGuide(true)}>
            See Plans
          </Button>
        </>
      ),
    },
  };

  const { header, subheader, renderActions } = contentState
    ? stateToHeaders[contentState]
    : stateToHeaders[ContentState.NoPlans];

  const showFilters =
    (loadedPlans && (contentState === ContentState.NoPlans || contentState === ContentState.Loading)) || forceFilters;

  // Log each content state change by adding state as query param.
  // This will be logged in the /analytics logging.
  useEffect(() => {
    if (!contentState) return;

    // Show confetti for the optimal / pareto cases
    if (contentState === ContentState.Optimal) {
      setConfetti(true);
    }

    router.push({
      pathname,
      query: {
        ...query,
        state: contentState,
      },
    });
  }, [contentState]);

  // Render full-page error when something goes wrong
  if (error) {
    return (
      <WizardLayout>
        <Flex p={10} alignItems="center" justifyContent="center" minH="60vh">
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Header as="h3">Give us a sec.</Header>

            <Body1>About ten million people are reshopping their Medicare coverage right now.</Body1>
            <Body1>Please try again later.</Body1>
            <Button onClick={() => router.reload()}>Refresh</Button>
          </Stack>
        </Flex>
      </WizardLayout>
    );
  }

  // if dsnp and switch recommended push to agent
  if (plan?.name?.includes('D-SNP') && contentState === ContentState.SwitchRecommended) {
    router.push(`${Routes.accountCallUs}/?context=aep&reason=${CallUsReason.DSNP}`);
    return null;
  }
  return (
    <WizardLayout>
      {!showDrxGuide && !bypassBreadcrumb && <AepBreadCrumbs />}
      {!loadedPlans && contentState === ContentState.Loading && (
        <Flex p={10}>
          <Spinner />
        </Flex>
      )}
      {!showDrxGuide ? (
        <Fade in={loadedPlans} style={{ width: '100%' }}>
          <Stack w="100%" spacing={8}>
            <Stack spacing={4} width="100%" minWidth="300px" maxWidth="800px" mx="auto">
              <Header as="h3">{header}</Header>
              <Body1>{typeof subheader === 'string' ? subheader : subheader()}</Body1>
            </Stack>
            <Collapse in={showFilters} style={{ width: '100%' }} unmountOnExit>
              <Box w="100%" mx="auto">
                <Filters
                  primaryProviders={primaryProviders}
                  specialistProviders={specialistProviders}
                  prescriptions={prescriptions}
                  pharmacy={pharmacy}
                  onFilterChange={onFilterChange}
                  planType={planType}
                  user={user}
                />
              </Box>
            </Collapse>
            <Stack alignItems={bypassBreadcrumb ? 'flex-start' : 'center'}>
              <Flex flexDirection={['column', 'column', 'row']} gap={2} alignItems="stretch" pb={4}>
                {renderActions()}
              </Flex>
              {contentState !== ContentState.NoPlans && (
                <Button
                  ml={0}
                  width="fit-content"
                  textDecoration="underline"
                  variant="link"
                  color="blue"
                  onClick={() => setForceFilters(!forceFilters)}
                  size="sm"
                >
                  {forceFilters ? 'Close Included View' : "See What's Included"}
                </Button>
              )}
            </Stack>
          </Stack>
        </Fade>
      ) : (
        <DrxGuide
          contentState={contentState}
          isDisabled={loadingRedirect || !recommendationsWithSavings.length}
          loading={loadingRedirect}
          planName={planName}
          planType={planType}
          recYear={recYear}
          relayState={relayState}
          samlBody={samlData?.getSAMLResponseBody?.response_body}
          setShowDrxGuide={setShowDrxGuide}
          topSavings={topRecommendation?.savings}
          userId={user?.id}
        />
      )}
      <DrugNotCoveredModal
        isOpen={isNotCoveredOpen}
        handleClose={handleNotCoveredClose}
        prescriptions={prescriptions}
      />
      <Confetti
        height={document.body.scrollHeight}
        onConfettiComplete={() => setConfetti(false)}
        recycle={false}
        run={confetti}
        width={document.body.scrollWidth}
      />
    </WizardLayout>
  );
};

export default RecommendationReview;
